import { Popover } from "@base-ui/react/popover"
import { Info } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * The tiles the Dashboard and Ops pages both draw with. They lived in
 * pages/dashboard.tsx until Ops split off; two pages importing one page is the
 * shape that turns a page file into a component library, so they moved here
 * alongside PlaceholderCard and StaleBanner, which are the same kind of thing.
 */

export function SectionHeading({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return (
    <div className="mb-4">
      <h2 className="text-4xl font-semibold tracking-tight">{title}</h2>
      {description && (
        <p className="text-xl text-muted-foreground mt-1 max-w-5xl leading-snug">
          {description}
        </p>
      )}
    </div>
  )
}

export function ChartCard({
  title,
  subtitle,
  children,
  className,
}: {
  title: string
  subtitle?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("rounded-lg border bg-card p-4", className)}>
      <div className="mb-3">
        <h3 className="font-semibold text-2xl">{title}</h3>
        {subtitle && (
          <p className="text-base text-muted-foreground mt-0.5">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  )
}

export function EventBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    generated: "bg-emerald-100 text-emerald-700 border-emerald-200",
    extended: "bg-blue-100 text-blue-700 border-blue-200",
    overwritten: "bg-amber-100 text-amber-700 border-amber-200",
  }
  const cls = map[type] ?? "bg-muted text-muted-foreground border-border"
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-sm font-medium border",
        cls,
      )}
    >
      {type}
    </span>
  )
}

/**
 * A caveat folded behind an info icon: the number and its one-line label stay
 * legible, the "yes but" only shows when someone asks for it.
 *
 * Base UI's Tooltip opens on hover only and its Popover opens on press only —
 * neither alone covers a dashboard read on a laptop AND on an iPad. The
 * Popover trigger's own `openOnHover` gives us both from one primitive, so
 * there's no custom hover state to own here.
 */
export function InfoHint({
  children,
  label = "Why this number reads the way it does",
  side = "top",
  className,
}: {
  children: React.ReactNode
  label?: string
  side?: "top" | "bottom" | "left" | "right"
  className?: string
}) {
  return (
    <Popover.Root>
      <Popover.Trigger
        openOnHover
        delay={120}
        closeDelay={80}
        aria-label={label}
        className={cn(
          "inline-flex size-4 shrink-0 translate-y-[2px] items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
          className,
        )}
      >
        <Info className="size-3.5" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side={side} sideOffset={6} className="isolate z-50">
          <Popover.Popup className="w-80 max-w-[calc(100vw-2rem)] rounded-lg border bg-popover p-3 text-sm leading-snug text-popover-foreground shadow-lg">
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
